import React, { useState, useEffect, useRef } from 'react';
import { Edit2 } from 'lucide-react';
import { AuthContainer } from './AuthContainer';
import { VerificationCode } from './VerificationCode';
import { useAuth } from './AuthContext';
import { apiPost } from '../services/api.service';
import { showError } from '../services/notification.service';
import { useTranslation } from '../i18n/TranslationContext';

export function EmailVerification() {
  const [code, setCode] = useState('');
  const [countdown, setCountdown] = useState(60); // Start at 60 seconds
  const [canResend, setCanResend] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [verificationToken, setVerificationToken] = useState('');
  const [lastSentTime, setLastSentTime] = useState<number>(Date.now());
  const { setCurrentStep, authData, updateAuthData } = useAuth();
  const { t } = useTranslation();
  const hasSentRef = useRef(false);

  // Send verification code on mount only if not rate limited
  useEffect(() => {
    if (authData.email && !verificationToken && !hasSentRef.current) {
      // Check if we should auto-send (coming from expired token)
      const shouldAutoSend = sessionStorage.getItem('autoSendEmail');
      if (shouldAutoSend) {
        sessionStorage.removeItem('autoSendEmail'); // Clear the flag
        hasSentRef.current = true;
        sendVerificationCode();
        return;
      }

      // Check if we have a recent send time stored
      const storedLastSent = sessionStorage.getItem(
        `email_last_sent_${authData.email}`
      );
      if (storedLastSent) {
        const timeSinceLast = Date.now() - parseInt(storedLastSent);
        const remainingMs = 60000 - timeSinceLast; // 60 seconds

        if (remainingMs > 0) {
          // Still in cooldown, just set the countdown
          const remainingSeconds = Math.ceil(remainingMs / 1000);
          setCountdown(remainingSeconds);
          setCanResend(false);
          // Show error notification
          showError(
            t('emailVerification.errors.rateLimitWait', { seconds: remainingSeconds })
          );
          return; // Don't send again
        }
      }

      // Additional check to prevent double-send in StrictMode
      const recentSession = sessionStorage.getItem('email_current_session');
      if (recentSession && Date.now() - parseInt(recentSession) < 2000) {
        // Another send happened within 2 seconds, skip this one
        return;
      }

      sessionStorage.setItem('email_current_session', Date.now().toString());
      hasSentRef.current = true;
      sendVerificationCode();
    }
  }, []);

  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    } else {
      setCanResend(true);
    }
  }, [countdown]);

  const sendVerificationCode = async () => {
    try {
      setIsLoading(true);
      setError('');

      const response = await apiPost<any>(
        '/verification/email/send',
        {
          email: authData.email,
          firstName: authData.firstName || 'User',
        },
        { notification: { showError: true } }
      );

      if (response.success) {
        // No token yet - will be created after verification
        // Store the send time for rate limiting
        sessionStorage.setItem(
          `email_last_sent_${authData.email}`,
          Date.now().toString()
        );
        // Reset countdown on successful send
        setCountdown(60);
        setCanResend(false);
        setLastSentTime(Date.now());
      } else if ((response as any).remainingSeconds) {
        // Handle rate limit error - don't show duplicate error in UI
        setCountdown((response as any).remainingSeconds);
        setCanResend(false);
        // The notification service will show the error message
      } else {
        setError(response.message || t('emailVerification.errors.sendFailed'));
      }
    } catch (err: any) {
      // Check if it's a rate limit error (429)
      if (err?.status === 429 && err?.data?.remainingSeconds) {
        // Don't show error in UI for rate limit - notification is enough
        setCountdown(err.data.remainingSeconds);
        setCanResend(false);
      } else {
        setError(
          err?.message || t('emailVerification.errors.sendFailedTryAgain')
        );
      }
      console.error('Email verification send error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCodeComplete = async (verificationCode: string) => {
    setCode(verificationCode);
    setIsLoading(true);
    setError('');

    try {
      const response = await apiPost('/verification/email/verify', {
        email: authData.email,
        code: verificationCode,
      });

      if (response.success) {
        // Store the new session token received after verification
        if ((response as any).verificationToken) {
          sessionStorage.setItem(
            'emailVerificationToken',
            (response as any).verificationToken
          );
          setVerificationToken((response as any).verificationToken);
        }
        setCurrentStep(2); // Go to phone number input
      } else {
        setError(response.message || t('emailVerification.errors.invalidCode'));
      }
    } catch (err: any) {
      setError(t('emailVerification.errors.verifyFailed'));
      console.error('Email verification error:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResend = async () => {
    setCode('');
    setError('');
    hasSentRef.current = true; // Prevent double sending on resend too
    await sendVerificationCode();
  };

  const handleEditEmail = () => {
    setCurrentStep(0); // Go back to sign in
  };

  const handleBack = () => {
    setCurrentStep(0);
  };

  return (
    <AuthContainer showBack showProgress onBack={handleBack}>
      <div className="text-center mb-8">
        <h1 className="text-[28px] font-bold text-[var(--ucm-text-dark)] mb-8 leading-tight">
          {t('emailVerification.title')}
        </h1>
        <div className="flex items-center justify-center space-x-2 mb-8">
          <p className="text-[var(--ucm-text-medium)]">
            {t('emailVerification.subtitle', { email: authData.email })}
          </p>
          <button
            onClick={handleEditEmail}
            className="text-[var(--ucm-blue-link)] hover:text-[var(--ucm-blue-link-hover)] p-1"
          >
            <Edit2 size={16} />
          </button>
        </div>
      </div>

      <div className="mb-8">
        <VerificationCode
          length={6}
          onComplete={handleCodeComplete}
          onCodeChange={setCode}
          value={code}
        />
        {error && (
          <p className="text-sm text-red-500 mt-2 text-center">{error}</p>
        )}
      </div>

      <div className="text-center mb-8 space-y-3">
        <p className="text-sm text-[var(--ucm-text-medium)]">
          {t('emailVerification.didntReceive')}{' '}
          {canResend ? (
            <button
              onClick={handleResend}
              className="text-[var(--ucm-blue-link)] hover:text-[var(--ucm-blue-link-hover)] hover:underline font-medium"
            >
              {t('emailVerification.resendCode')}
            </button>
          ) : (
            <span className="text-[var(--ucm-text-light)]">
              {t('emailVerification.resendIn', { seconds: countdown })}
            </span>
          )}
        </p>
        
        <div className="bg-[var(--ucm-light-blue)] border border-[var(--ucm-info-blue)] rounded-lg p-3">
          <p className="text-xs text-[var(--ucm-dark-blue)]">
            {t('emailVerification.spamFolderNote')}
          </p>
        </div>
      </div>

      <button
        onClick={() => code.length === 6 && handleCodeComplete(code)}
        disabled={code.length !== 6 || isLoading}
        className="w-full h-14 bg-[var(--ucm-primary-teal)] text-white font-semibold rounded-full hover:bg-[var(--ucm-primary-teal-hover)] disabled:bg-[var(--ucm-text-light)] disabled:cursor-not-allowed transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-[var(--ucm-primary-teal)] focus:ring-offset-2"
      >
        {isLoading ? t('emailVerification.verifying') : t('common.continue')}
      </button>
    </AuthContainer>
  );
}
